import PropTypes from "prop-types";
import {
  Banner,
  Chip,
  LinkButton,
  Muted,
  QueueColumn,
  QueueHeader,
  QueueRow,
  QueueScroll,
  RowLine,
  RowName,
  RowNote,
  RowTop,
  ScopeRow,
  ScopeTab,
  Title,
} from "../BoardAppealsStyled";
import { daysLeftLabel, deadlineTone, decidedTone, formatDay } from "../utils";

const SCOPES = [
  { key: "pending", label: "To decide" },
  { key: "decided", label: "Decided" },
];

/**
 * An empty pile has two causes that must never look alike: nothing is
 * waiting, or the pull from Litify is failing.
 */
const emptyMessage = (scope, lastSync) => {
  if (scope === "decided") return { text: "No decisions recorded yet." };
  if (lastSync?.enabled === false) {
    return {
      text:
        "Litify is not read in this environment, so only Board decisions " +
        "sent by staff appear here.",
      failed: true,
    };
  }
  if (lastSync?.errors) {
    return {
      text:
        "Could not read the waiting Court intakes from Litify" +
        (lastSync.error ? `: ${lastSync.error}` : "") +
        ". This list is not up to date.",
      failed: true,
    };
  }
  return { text: "Nothing waiting on a decision." };
};

const AppealQueue = ({
  scope,
  onScope,
  items,
  counts,
  lastSync,
  loading,
  error,
  selectedId,
  onSelect,
  refreshing,
  onRefresh,
}) => {
  const empty = emptyMessage(scope, lastSync);
  return (
    <QueueColumn>
      <QueueHeader>
        <Title>Board appeals</Title>
        <ScopeRow>
          {SCOPES.map(({ key, label }) => (
            <ScopeTab
              key={key}
              type="button"
              $active={scope === key}
              aria-pressed={scope === key}
              onClick={() => onScope(key)}
            >
              {label}
              {key === "pending" && counts?.pending > 0
                ? ` · ${counts.pending}`
                : ""}
            </ScopeTab>
          ))}
          <LinkButton type="button" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? "Checking…" : "Check Litify"}
          </LinkButton>
        </ScopeRow>
      </QueueHeader>
      <QueueScroll>
        {error ? <Banner role="alert">{error}</Banner> : null}
        {loading && !items.length ? <Muted>Loading…</Muted> : null}
        {!loading && !error && !items.length ? (
          empty.failed ? (
            <Banner role="alert">{empty.text}</Banner>
          ) : (
            <Muted>{empty.text}</Muted>
          )
        ) : null}
        {items.map((row) => (
          <QueueRow
            key={row.id}
            type="button"
            $active={row.id === selectedId}
            aria-current={row.id === selectedId ? "true" : undefined}
            onClick={() => onSelect(row.id)}
          >
            <RowTop>
              <RowName>{row.clientName || "Unnamed client"}</RowName>
              {scope === "decided" ? (
                <Chip $tone={decidedTone(row.status)}>{row.statusLabel}</Chip>
              ) : (
                <Chip $tone={deadlineTone(row)}>
                  {daysLeftLabel(row.daysLeft)}
                </Chip>
              )}
            </RowTop>
            <RowLine>
              {row.boardDate
                ? `Board decision ${formatDay(row.boardDate)}`
                : "Board date not on file"}
              {row.sourceLabel ? ` · ${row.sourceLabel}` : ""}
              {!row.documentId ? " · no decision in the file" : ""}
            </RowLine>
            {row.requestNote ? <RowNote>“{row.requestNote}”</RowNote> : null}
          </QueueRow>
        ))}
      </QueueScroll>
    </QueueColumn>
  );
};

AppealQueue.propTypes = {
  scope: PropTypes.string.isRequired,
  onScope: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  counts: PropTypes.object,
  lastSync: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  selectedId: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  refreshing: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
};

export default AppealQueue;
